export const SKIP_FIELDS = ['clinicId', 'id', 'createdAt', 'modifiedAt'];

export const SERVICE_LABELS = {
    reminder: 'Appointment Reminders',
    recall: 'Patient Recalls',
    reputation: 'Reputation Management',
    survey: 'Patient Surveys',
    isSurvey: 'Survey Reminders',
    booking: 'Online Booking',
    twoWayText: 'Two-way Text Messaging',
    isAntispamLaw: 'Anti-Spam Compliance',
    isInvalidSummary: 'Invalid Details Summary',
    isAppointments: 'Appointments',
    isArticle: 'Articles',
    isOpticalDispensary: 'Optical Dispensary',
    webInquiry: 'Web Inquiry',
    reminderEmail: 'Email Reminders',
    reminderCall: 'Phone Reminders',
    reminderSms: 'SMS Reminders',
    newsletterEmail: 'Email Newsletter',
    newsletterSms: 'SMS Newsletter',
    blastEmail: 'Email Blasts',
    blastSms: 'SMS Blasts',
    blastCall: 'Call Campaigns',
};

export const SERVICE_GROUPS = [
    {
        title: 'Core Features',
        keys: [
            'reminder',
            'recall',
            'reputation',
            'survey',
            'isSurvey',
            'booking',
            'twoWayText',
            'isAntispamLaw',
            'isInvalidSummary',
            'isAppointments',
            'isArticle',
            'isOpticalDispensary',
            'webInquiry',
        ],
    },
    {
        title: 'Reminder Channels',
        keys: ['reminderEmail', 'reminderCall', 'reminderSms'],
    },
    {
        title: 'Newsletter',
        keys: ['newsletterEmail', 'newsletterSms'],
    },
    {
        title: 'Blast Campaigns',
        keys: ['blastEmail', 'blastSms', 'blastCall'],
    },
];

export const ALL_SERVICE_KEYS = SERVICE_GROUPS.flatMap((group) => group.keys);

export const getServiceLabel = (key) => SERVICE_LABELS[key] || key;

export const getServiceIconClass = (key) => {
    switch (key) {
        case 'reminder':
        case 'reminderEmail':
        case 'reminderCall':
        case 'reminderSms':
            return 'bi-bell';
        case 'recall':
            return 'bi-calendar-check';
        case 'reputation':
            return 'bi-star';
        case 'survey':
        case 'isSurvey':
            return 'bi-clipboard-check';
        case 'booking':
            return 'bi-bookmark-check';
        case 'twoWayText':
            return 'bi-chat-left-text';
        case 'isAntispamLaw':
            return 'bi-shield-lock';
        case 'isInvalidSummary':
            return 'bi-exclamation-octagon';
        case 'isAppointments':
            return 'bi-calendar-event';
        case 'isArticle':
            return 'bi-file-text';
        case 'isOpticalDispensary':
            return 'bi-eyeglasses';
        case 'webInquiry':
            return 'bi-globe2';
        case 'newsletterEmail':
        case 'newsletterSms':
            return 'bi-envelope';
        case 'blastEmail':
        case 'blastSms':
        case 'blastCall':
            return 'bi-megaphone';
        default:
            return 'bi-gear';
    }
};

export const getServiceIconTone = (key) => {
    switch (key) {
        case 'reminder':
        case 'reminderEmail':
        case 'reminderCall':
        case 'reminderSms':
            return 'reminder';
        case 'recall':
            return 'recall';
        case 'reputation':
            return 'reputation';
        case 'survey':
        case 'isSurvey':
            return 'survey';
        case 'booking':
        case 'isAppointments':
            return 'booking';
        case 'isArticle':
            return 'default';
        case 'isOpticalDispensary':
            return 'optical';
        case 'webInquiry':
            return 'inquiry';
        case 'twoWayText':
            return 'messaging';
        case 'isAntispamLaw':
            return 'compliance';
        case 'isInvalidSummary':
            return 'compliance';
        case 'newsletterEmail':
        case 'newsletterSms':
            return 'newsletter';
        case 'blastEmail':
        case 'blastSms':
        case 'blastCall':
            return 'blast';
        default:
            return 'default';
    }
};

/** Normalize API settings so every known feature key is present (default false). */
export const withServiceDefaults = (settings = {}) => {
    const normalized = { ...settings };

    ALL_SERVICE_KEYS.forEach((key) => {
        normalized[key] = settings?.[key] === true;
    });

    return normalized;
};

export const getServiceEntries = (settings) => {
    const normalized = withServiceDefaults(settings);
    const groupedKeys = new Set(ALL_SERVICE_KEYS);
    const entries = [];

    SERVICE_GROUPS.forEach((group) => {
        const items = group.keys.map((key) => ({
            key,
            value: normalized[key] === true,
            group: group.title,
        }));

        if (items.length > 0) {
            entries.push({ title: group.title, items });
        }
    });

    const otherItems = Object.entries(settings || {})
        .filter(([key]) => !SKIP_FIELDS.includes(key) && !groupedKeys.has(key))
        .map(([key, value]) => ({ key, value: value === true, group: 'Other' }));

    if (otherItems.length > 0) {
        entries.push({ title: 'Other', items: otherItems });
    }

    return entries;
};

export const countEnabledServices = (settings) => {
    const normalized = withServiceDefaults(settings);
    const serviceEntries = Object.entries(normalized).filter(([key]) => ALL_SERVICE_KEYS.includes(key));
    const enabled = serviceEntries.filter(([, value]) => value === true).length;

    return {
        enabled,
        disabled: serviceEntries.length - enabled,
        total: serviceEntries.length,
    };
};
